/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable no-script-url */
import { SmlsButton, SmlsLogoHeader } from '@smiles/smiles-ui-kit-react';
import moment, { Moment } from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { CounterInput, Icon, SmlsAutoCompleteSearch, SmlsCoin, Tooltip } from '..';
import { IAutoCompleteSearch } from '../AutoCompleteSearch/types';
import SmlsCalendar, { onChangeType } from '../Calendar/SmlsCalendar';

import { Helpers } from '@smiles/core-utils';

import { useAppDispatch, useAppSelector } from '../../hooks/useRedux';
import { DETAILS, RESULTS } from '../../routes';
import { ISearchReduxState, searchActions } from '../../store/duck/search';
import { capitalizeFirstLetter } from '../../utils';
import { parseSearchPanelDuckToQueryParams } from '../../utils/filtersSearch';
import { handleClosePopUp } from '../../utils/genericHandles';
import { RecentSearchesType, setRecentSearch } from '../../utils/localStorage';
import CrossSell from '../CrossSell';
import { DatalayerServices } from '../../services/datalayer';
import { ProductConfigServices } from '../../services/productConfig';
import type { RoomInputData } from '../CounterInput';
import SearchPanelBrowserMobile from '../SearchPanelBrowser/SearchPanelBrowserMobile';
import { GetPlacesService } from '../../services/core/getPlaces';

interface ISearchPanelProps {
  userSearchData?: IAutoCompleteSearch<RecentSearchesType>['initialList'];
  hideFixedHeader?: boolean;
  callBackSubmit?: () => void;
  variant?: 'home' | 'results';
  showBrowser?: boolean;
}

type SearchOutput = {
  id: number;
  name: string;
  description: string;
  type: string;
  lat?: number;
  lng?: number;
};

interface ICalendarDates {
  startDate: Moment | string | null;
  endDate: Moment | string | null;
}

const DATE_FORMAT = 'YYYY-MM-DD';

const SearchPanel = ({
  userSearchData,
  hideFixedHeader,
  variant = 'home',
  showBrowser = false,
  callBackSubmit,
}: ISearchPanelProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const isMobile = useAppSelector(({ generics }) => generics.isMobile);
  const isCrossSellChecked = useAppSelector(({ crossSell }) => crossSell.isChecked);
  const searchPanelDuck: ISearchReduxState = useAppSelector((duck) => duck.search);
  const userData = useAppSelector(({ user }) => user.member);
  const [isSearchPanelOpen, setIsSearchPanelOpen] = useState<boolean>(false);
  const [isPanelFocused, setIsPanelFocused] = useState<boolean>(false);
  const [autoCompleteSearchFocus, setAutoCompleteSearchFocus] = useState<boolean>(false);
  const [autoCompleteSearchHasError, setAutoCompleteSearchHasError] = useState<boolean>(false);
  const [calendarFocus, setCalendarFocus] = useState<boolean>(false);
  const [calendarDates, setCalendarDates] = useState<ICalendarDates>({
    startDate: searchPanelDuck.checkin || null,
    endDate: searchPanelDuck.checkout || null,
  });
  const [checkinAntecedence, setCheckingAntecedence] = useState<string>('0');
  const [calendarHasError, setCalendarHasError] = useState<boolean>(false);
  const [isCounterInputOpen, setIsCounterInputOpen] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>(searchPanelDuck.name || '');
  const [isMoreOptionsDisabled, setIsMoreOptionsDisabled] = useState<boolean>(true);
  const [isMilesInfoModalOpen, setIsMilesInfoModalOpen] = useState<boolean>(false);
  const [prevScrollPos, setPrevScrollPos] = useState(0);
  const [crossSellInputTooltipTrigger, setCrossSellInputTooltipTrigger] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deviceWidth, setDeviceWidth] = useState(window.innerWidth);
  const modalRef = useRef<HTMLDivElement>(null);

  const urlSearchParams = new URLSearchParams(window.location.search);
  const paramsObject = Object.fromEntries(urlSearchParams.entries());

  const hasCrossSellQuery = urlSearchParams.get('crossSell') === 'true';

  const calendarHolidaysList = useMemo(() => {
    const holidaysList = Helpers.getHolidays();
    return holidaysList.map(({ date, name }) => ({ date: moment(date), description: name }));
  }, []);

  const triggerCrossSellTooltip = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const crossSellQuery = urlParams.get('crossSell');

    if (crossSellQuery) {
      const decodedValue = decodeURIComponent(crossSellQuery as string);
      return decodedValue === 'true';
    }

    return false;
  }, []);

  const hideTooltips = () => {
    const tooltips = document.querySelectorAll('.hotels-search-panel-cross-sell-tooltip');
    setTimeout(() => {
      tooltips.forEach((tooltip) => {
        tooltip.classList.remove('visible');
      });
    }, 5000);
  };

  useEffect(() => {
    const tooltips = document.querySelectorAll('.hotels-search-panel-cross-sell-tooltip');
    if (triggerCrossSellTooltip) {
      tooltips.forEach((tooltip) => {
        tooltip.classList.add('visible');
      });

      const isFocusedScreen = document.visibilityState === 'visible';

      if (isFocusedScreen) {
        hideTooltips();
      }
    }

    setCrossSellInputTooltipTrigger(false);
    window.addEventListener('focus', hideTooltips);
  }, [triggerCrossSellTooltip]);

  useEffect(() => {
    const onGetCheckinAntecedence = async () => {
      const response = await ProductConfigServices.getCheckinAntecedence();

      if (response) {
        setCheckingAntecedence(response.checkinAntecedence);
      }
    };

    onGetCheckinAntecedence();

    if (hasCrossSellQuery) {
      searchByUrlParams();
    }
  }, []);

  useEffect(() => {
    const onMobileScroll = () => {
      const headerSmiles = document.querySelector<HTMLDivElement>('#smls-hf-header-default');
      const searchPanelMobile = document.querySelector<HTMLDivElement>('#search-panel-mobile');
      const isShowFullSearchPanelMobile = window.scrollY > 64;

      if (!searchPanelMobile || !headerSmiles) {
        return;
      }

      if (!isShowFullSearchPanelMobile) {
        searchPanelMobile.classList.remove('fixed');
        searchPanelMobile.style.top = '112px';

        headerSmiles.style.position = 'initial';
        headerSmiles.style.zIndex = '2';
        return;
      }

      searchPanelMobile.classList.add('fixed');

      if (window.scrollY > prevScrollPos) {
        headerSmiles.style.zIndex = '2';

        searchPanelMobile.style.top = '0px';
      } else {
        headerSmiles.style.position = 'fixed';
        headerSmiles.style.zIndex = '1000';

        searchPanelMobile.style.top = '64px';
      }

      setPrevScrollPos(window.scrollY);
    };

    

    const onDesktopScroll = () => {
      const searchPanelFixed = document.querySelector('.search-panel-fixed') as HTMLDivElement;
      const searchPanel = document.querySelector('#search-panel') as HTMLDivElement;

      const { bottom } = searchPanel.getBoundingClientRect();

      if (bottom < 0) {
        searchPanelFixed.classList.add('visible');
        searchPanel.style.marginBottom = '102px';
      } else {
        searchPanelFixed.classList.remove('visible');
        searchPanel.style.marginBottom = '0';
      }

      const loggedUserInfo = document.querySelector('.recent-search-wrapper');
      const moreOptionsButton = document.querySelector('.more-options');
      const divider = document.querySelector('.miles-info-divider');

      if (loggedUserInfo && moreOptionsButton && divider) {
        const loggedUserInfoRect = loggedUserInfo.getBoundingClientRect();
        const bottomOffset = 96; // altura do painel fixo;

        if (loggedUserInfoRect.bottom - bottomOffset < 0) {
          moreOptionsButton.classList.add('visible');
          divider.classList.add('visible');
          setIsMoreOptionsDisabled(false);
        } else {
          moreOptionsButton.classList.remove('visible');
          divider.classList.remove('visible');
          setIsMoreOptionsDisabled(true);

          const milesInfoModal = document.querySelector('.miles-info-modal');
          if (milesInfoModal && milesInfoModal.classList.contains('open')) {
            milesInfoModal.classList.remove('open');
          }
        }
      }
    };

    if (isMobile && !showBrowser) {
      document.removeEventListener('scroll', onMobileScroll);
      document.addEventListener('scroll', onMobileScroll, { passive: true });
    }

    if (!isMobile) {
      document.removeEventListener('scroll', onDesktopScroll);
      document.addEventListener('scroll', onDesktopScroll, { passive: true });
    }

    return () => {
      document.removeEventListener('scroll', onMobileScroll);
      document.removeEventListener('scroll', onDesktopScroll);
    };
  }, [isMobile, showBrowser, prevScrollPos]);

  useEffect(() => handleClosePopUp(modalRef, () => setIsMilesInfoModalOpen(false)), []);

  useEffect(() => {
    if (autoCompleteSearchFocus) {
      setAutoCompleteSearchFocus(false);
    }
  }, [autoCompleteSearchFocus]);

  // useEffect to hide portal header on when mobile search panel is open (bug on Safari)
  useEffect(() => {
    const portalHeader = document.querySelector<HTMLDivElement>('#sspa-header');

    if (portalHeader) {
      if (isMobile && isSearchPanelOpen) {
        portalHeader.style.display = 'none';
      } else {
        portalHeader.removeAttribute('style');
      }
    }

    return () => {
      if (portalHeader) {
        portalHeader.removeAttribute('style');
      }
    };
  }, [isMobile, isSearchPanelOpen]);

  useEffect(() => {
    const handleResize = () => setDeviceWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
      window.dataLayer = window.dataLayer || [];
  }, []);

  const pushEventToDataLayer = (terms, numPassengers, dateBetween) => {
    window.dataLayer.push({
      event: 'search',
      prod_type: 'HOTEIS',
      search_terms: terms,
      passagers: numPassengers,
      period: dateBetween
    });
  }

  const getDataLayerParams = (roomsNumber: RoomInputData[]) => {
    const { region, checkin, checkout } = searchPanelDuck;
    const rooms = roomsNumber
    const isNationalDestin = region.toLowerCase().includes('brasil');
    const destinationType = isNationalDestin ? 'Nacional' : 'Internacional';
  
    const passengers = rooms.reduce((room, { adults, children }) => {
      room.adults += adults;
      room.children += children.length;
      return room;
    }, { adults: 0, children: 0 });
  
    const roomCount = rooms.length;
  
    return {
      searchTerms: `${destinationType} - ${roomCount} ${roomCount === 1 ? 'quarto' : 'quartos'}`,
      passengersNum: `Adultos=${passengers.adults} | Criancas=${passengers.children} | Bebe=0`,
      period: `${formatDate(checkin)} | ${formatDate(checkout)}`
    };
  
  };

  const toggleFixedPanelModal = () => setIsMilesInfoModalOpen((prevState) => !prevState);

  const validateInputs = () => {
    let output = true;

    if (!searchValue) {
      setAutoCompleteSearchHasError(true);
      output = false;
    }

    if (!(searchPanelDuck.checkin && searchPanelDuck.checkout)) {
      setCalendarHasError(true);
      output = false;
    }

    return output;
  };

  const handleDesktopScrolling = () => {
    const yOffset = -48;
    const element = document.querySelector('#search-panel')!;
    const y = element.getBoundingClientRect().top + window.scrollY + yOffset;

    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  const handleInputFocus = () => {
    setIsSearchPanelOpen(true);

    if (!hasCrossSellQuery) {
      setSearchValue('');
    }

    setAutoCompleteSearchHasError(false);
    setCalendarFocus(false);
  };

  const handleInputSearch = async (inputValue: string) => {
    interface IRegionsData {
      dsCoordenada: { type: string; coordinates: number[] };
      id: number;
      idContinente: number | null;
      idPais: number | null;
      itRegiaoList: any | null;
      nmRegiao: string;
      qtPesquisa: number;
      sbRegiao: string;
      sgIata: string | null;
      stPoligono: boolean;
      tipoId: number;
      tpDestino: string;
      tpMotor: string;
    }

    interface IHotelsData {
      cdLatitude: number;
      cdLongitude: number;
      id: number;
      idRegiaoList: number[];
      nmDestino: string;
      nmHotel: string;
      nmLogradouro: string;
      nmRegiao: string;
      nrCep: string;
      nrTelefone: string;
      qtEstrela: number;
      qtPesquisa: number;
      regiaoId: number;
    }

    const regionsResponse = await fetch(
      `https://reservas.smilesviagens.com.br/integra/api/regiao/pesquisar/${inputValue}/null/pt_BR`,
    );
    const regionsData: IRegionsData[] = await regionsResponse.json();

    const hotelsResponse = await fetch(
      `https://reservas.smilesviagens.com.br/integra/api/hotel/pesquisar/${inputValue}/0/null/null/null`,
    );
    const hotelsData: IHotelsData[] = await hotelsResponse.json();

    let formattedRegionsList: SearchOutput[] = [];
    let formattedHotelsList: SearchOutput[] = [];

    if (regionsData) {
      const type = 'place';
      formattedRegionsList = regionsData.map(({ id, nmRegiao, sbRegiao, dsCoordenada }) => {
        return {
          id,
          name: nmRegiao,
          description: sbRegiao,
          type,
          lat: dsCoordenada?.coordinates?.[0] || undefined,
          lng: dsCoordenada?.coordinates?.[1] || undefined,
        };
      });
    }

    if (hotelsData) {
      const type = 'hotel';
      formattedHotelsList = hotelsData.map(({ id, nmHotel, nmRegiao, cdLatitude, cdLongitude }) => ({
        id,
        name: nmHotel,
        description: nmRegiao,
        type,
        lat: cdLatitude,
        lng: cdLongitude,
      }));
    }

    const output: SearchOutput[] = [...formattedRegionsList, ...formattedHotelsList];

    return output;
  };

  const handleInputSelecteditem = (item: SearchOutput, autoOpenCalendar: boolean = true) => {
    setAutoCompleteSearchHasError(false);
    setSearchValue(`${item.name}, ${item.description}`);

    const isNationalDestin = item.description.toLocaleLowerCase().includes('brasil');

    const datalayerRegionName = `${item.name} ${item.description}`;

    window.sessionStorage.setItem(
      DatalayerServices.GA4.DATALAYER_REGION,
      isNationalDestin ? `Nacional: ${datalayerRegionName}` : `Internacional: ${datalayerRegionName}`,
    );

    const output: any = {
      name: capitalizeFirstLetter(item.name),
      region: item.description,
      type: item.type,
      id: item.id,
    };

    dispatch(searchActions.setSearchDestination(output));
    setCalendarFocus(isMobile ? autoOpenCalendar : !hasCrossSellQuery);
  };

  const handleCalendarPrevious = () => {
    setCalendarFocus(false);
    setAutoCompleteSearchFocus(true);
  };

  const handleCalendarSkip = () => {
    setCalendarFocus(false);
    setIsCounterInputOpen(true);
  };

  const handleCalendarChange = (dates: onChangeType) => {
    setCalendarHasError(false);
    setCalendarDates(dates);

    if (dates.startDate && dates.endDate) {
      const output = {
        checkin: moment(dates.startDate).format(DATE_FORMAT),
        checkout: moment(dates.endDate).format(DATE_FORMAT),
      };
      dispatch(searchActions.setSearchDates(output));
    }
  };

  const handleCalendarClear = () => {
    setCalendarDates({ startDate: null, endDate: null });

    dispatch(searchActions.setSearchDates({ checkin: '', checkout: '' }));
  };

  const handleCalendarSubmit = () => {
    if (calendarDates) {
      const output = {
        checkin: moment(calendarDates.startDate, moment.ISO_8601).format(DATE_FORMAT),
        checkout: moment(calendarDates.endDate, moment.ISO_8601).format(DATE_FORMAT),
      };

      if (!!calendarDates.startDate && !!calendarDates.endDate) {
        dispatch(searchActions.setSearchDates(output));
      }

      setCalendarHasError(false);
      setCalendarFocus(false);
      setIsCounterInputOpen(true);
    }
  };

  const createRooms = (totalAdults: number, children: { id: number; age: number }[]) => {
    const maxAdultsPerRoom = 4;

    return Array.from({ length: Math.ceil(totalAdults / maxAdultsPerRoom) }, (_, i) => ({
      id: i + 1,
      adults: Math.min(maxAdultsPerRoom, totalAdults - i * maxAdultsPerRoom),
      children: i === 0 ? children : [],
    }));
  };

  const searchByUrlParams = async () => {
    const convertTimeToDate = (date: string | number) => new Date(Number(date)).toISOString().split('T')[0];

    const childrenNumber = Number(paramsObject.children) + Number(paramsObject.infants);
    const children = Array.from({ length: childrenNumber }, (_, i) => ({
      id: i + 1,
      age: 1,
    }));

    const dataRooms = createRooms(Number(paramsObject.adults), children);
    const searchDestin = await handleInputSearch(paramsObject.city);

    if (searchDestin.length === 0) {
      return;
    }

    const departureDate = Number(paramsObject.departureDate || paramsObject.departureDate2);
    const checkin = convertTimeToDate(departureDate);

    const departureDateTime = new Date(departureDate);
    const dayAfterDeparture = departureDateTime.setDate(departureDateTime.getDate() + 1);
    const isSameDate = !!paramsObject.returnDate && checkin === convertTimeToDate(paramsObject.returnDate);

    const returnDate = isSameDate ? dayAfterDeparture : paramsObject.returnDate || dayAfterDeparture;
    const checkout = convertTimeToDate(returnDate);

    handleInputSelecteditem(searchDestin[0], false);
    dispatch(searchActions.setSearchRoomsAndTravelers(dataRooms));
    dispatch(
      searchActions.setSearchDates({
        checkin,
        checkout,
      }),
    );
  };

  const onGetCrossSell = async (pageRoute: string, queryParams: string) => {
    setIsSubmitting(true);

    const isNationalDestin = searchPanelDuck.region.toLocaleLowerCase().includes('brasil');
    const destinationType = isNationalDestin ? 'national' : 'international';
    const destinCity = searchPanelDuck.name;

    let places = await GetPlacesService.execute(destinCity, destinationType);

    if (!places.length) {
      const destinRegion = searchPanelDuck.region.split(',')[0];

      places = await GetPlacesService.execute(destinRegion, destinationType);
    }

    const codePlace = String(places[0]?.code);

    const pickUpDate = `${searchPanelDuck.checkin}T10:00:00`;
    const returnDate = `${searchPanelDuck.checkout}T10:00:00`;

    const nationalParams = {
      pickUpCode: codePlace,
      returnCode: codePlace,
      pickUpDate,
      returnDate,
      crossSell: 'true',
    };

    const internationalParams = {
      agenciaRetirada: codePlace,
      agenciaDevolucao: codePlace,
      dataHoraRetirada: pickUpDate,
      dataHoraDevolucao: returnDate,
      crossSell: 'true',
    };

    const baseUrlNacional = new URL(`${window.location.origin}/mfe/carros/nacional/resultados`);
    baseUrlNacional.search = new URLSearchParams(nationalParams).toString();

    const baseUrlInternacional = new URL(`${window.location.origin}/mfe/carros/internacional/resultados`);
    baseUrlInternacional.search = new URLSearchParams(internationalParams).toString();

    const baseUrl = isNationalDestin ? baseUrlNacional : baseUrlInternacional;

    window.open(`${pageRoute}?${queryParams}`, '_system');

    window.location.assign(baseUrl.toString());
  };

  const formatDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-');
    return `${day}-${month}-${year}`;
  };

  const handleSubmit = () => {
    const isValidated = validateInputs();
    if (isValidated || hasCrossSellQuery) {
      setRecentSearch({ destination: searchValue, date: calendarDates as any, url: '' });

      const searchPanelQueryParams = parseSearchPanelDuckToQueryParams(searchPanelDuck);

      let pageRoute = RESULTS;
      let queryParams = searchPanelQueryParams;

      if (searchPanelDuck.type === 'hotel') {
        pageRoute = DETAILS;
        queryParams = `${searchPanelQueryParams}&hotel=${searchPanelDuck.id}`;
      }

      const dataLayerData = getDataLayerParams(searchPanelDuck.rooms)
      pushEventToDataLayer(dataLayerData.searchTerms, dataLayerData.passengersNum, dataLayerData.period)

      if (variant === 'results') {
        if (searchPanelDuck.type === 'hotel') {
          window.open(`${pageRoute}?${queryParams}`, '_self');
        } else {
          history.replace(`${pageRoute}?${queryParams}`);
        }
      } else if (isCrossSellChecked && !isMobile) {
        onGetCrossSell(pageRoute, queryParams);
      } else {
        window.open(`${pageRoute}?${queryParams}`, '_self');
      }

      if (callBackSubmit) {
        setTimeout(() => {
          callBackSubmit();
        }, 500);
      }
    }
  };

  const handleCounterInputReturn = () => {
    setIsCounterInputOpen(false);
    setCalendarFocus(true);
  };

  const handleCounterInputMobileSubmit = (data: RoomInputData[], shouldSubmit: boolean) => {
    if (shouldSubmit) {
      setRecentSearch({ destination: searchValue, date: calendarDates as any, url: '' });
      setIsCounterInputOpen(false);
      dispatch(searchActions.setSearchRoomsAndTravelers(data));
      const mobileSearchPanelQueryParams = parseSearchPanelDuckToQueryParams({ ...searchPanelDuck, rooms: data });

      let pageRoute = RESULTS;
      let queryParams = mobileSearchPanelQueryParams;

      if (searchPanelDuck.type === 'hotel') {
        pageRoute = DETAILS;
        queryParams = `${mobileSearchPanelQueryParams}&hotel=${searchPanelDuck.id}`;
      }

      const dataLayerData = getDataLayerParams(data)
      pushEventToDataLayer(dataLayerData.searchTerms, dataLayerData.passengersNum, dataLayerData.period)

      if (isCrossSellChecked) {
        onGetCrossSell(pageRoute, queryParams);
      } else {
        window.open(`${pageRoute}?${queryParams}`, '_self');
      }
    }
  };

  const handleCounterInputSubmit = (data: RoomInputData[]) => {
    dispatch(searchActions.setSearchRoomsAndTravelers(data));
  };

  const renderSearchPanelDesktop = () => (
    <>
      <section className="search-panel-container" id="search-panel" onClick={handleDesktopScrolling}>
        <section className="search-panel-dropdown-wrapper">
          <CounterInput
            initialData={variant === 'results' || hasCrossSellQuery ? searchPanelDuck.rooms : undefined}
            onSubmit={handleCounterInputSubmit}
          />
          <CrossSell />
        </section>

        <section className="search-panel-inputs-wrapper">
          <Tooltip
            tooltipClassName="hotels-search-panel-cross-sell-tooltip"
            containerClassName="hotels-search-panel-cross-sell-tooltip-container"
            text={t('smlsAutoCompleteSearch.crossSell.searchInput')}
            position="bottom-left"
            trigger={crossSellInputTooltipTrigger}
            offset={{ right: '42px', top: '2px' }}
          >
            <SmlsAutoCompleteSearch
              className="search-input"
              inputId="hotels-auto-complete-search"
              placeholder={
                autoCompleteSearchHasError
                  ? t('smlsAutoCompleteSearch.errorPlaceholder')
                  : t('smlsAutoCompleteSearch.placeholder')
              }
              inputErrorId="input-error"
              errorMessage={
                autoCompleteSearchHasError
                  ? t('smlsAutoCompleteSearch.errorMessage')
                  : t('smlsAutoCompleteSearch.invalidField')
              }
              forceError={autoCompleteSearchHasError}
              initialList={userSearchData}
              notFoundTitle={t('smlsAutoCompleteSearch.notFoundTitle')}
              notFoundSubtitle={t('smlsAutoCompleteSearch.notFoundSubtitle')}
              onFocus={handleInputFocus}
              onSearch={handleInputSearch}
              onSelectedItem={handleInputSelecteditem}
              value={searchValue}
            />
          </Tooltip>

          <SmlsCalendar
            id="search-panel-calendar"
            startDateId="search-panel-calendar-start-date-input"
            endDateId="search-panel-calendar-end-date-input"
            maxDate={11}
            minDate={0}
            startDatePlaceholderText={t('smlsCalendar.startDatePlaceholder')}
            endDatePlaceholderText={t('smlsCalendar.endDatePlaceholder')}
            forceFocus={calendarFocus}
            forceError={calendarHasError}
            errorMessage={t('smlsCalendar.errorMessage')}
            onChange={handleCalendarChange}
            onSubmit={handleCalendarSubmit}
            onClear={handleCalendarClear}
            isOutsideRange={(day) => day.isBefore(moment().endOf('day').add(checkinAntecedence, 'days'))}
            datesListHighlight={calendarHolidaysList}
            startDateInitialValue={searchPanelDuck.checkin}
            endDateInitialValue={searchPanelDuck.checkout}
          />

          <SmlsButton
            text={t('common.searchButtonText')}
            isLoading={isSubmitting}
            onClick={handleSubmit}
            className="search-panel-button"
          />
        </section>
      </section>

      {!hideFixedHeader && (
        <section className="search-panel-fixed">
          <div className="search-panel-fixed-content-wrapper">
            <div className="logo-wrapper">
              <SmlsLogoHeader className="smiles-logo" />
            </div>

            <div className="mini-panel-wrapper">
              <button type="button" className="mini-panel" onClick={handleDesktopScrolling}>
                <div className="content">
                  <i className="material-icons round">search</i>
                  <span>{t('common.findNewPlaces')}</span>
                </div>
              </button>
            </div>

            {userData ? (
              <div className="miles-info" ref={modalRef}>
                <span>{`${Number(userData.availableMiles).toLocaleString('pt-BR')} ${t('common.miles')}`}</span>
                <SmlsCoin color="#333333" className="miles-coin" />
                <div className="miles-info-divider" />
                <button
                  type="button"
                  className="more-options"
                  disabled={isMoreOptionsDisabled}
                  onClick={toggleFixedPanelModal}
                >
                  <i className="material-icons round">more_vert</i>
                </button>
                <div className={`miles-info-modal ${isMilesInfoModalOpen ? 'open' : ''}`}>
                  <span>{t('home.recentSearch.myAccommodations')}</span>
                  <span>{t('home.recentSearch.help')}</span>
                </div>
              </div>
            ) : (
              <div className="login-button-wrapper">
                <a href="javascript:smls.hf.Controller.callLogin()" className="login-button">
                  <i className="smls-hf-icon profile-black" />
                  <span>{t('common.login')}</span>
                </a>
              </div>
            )}
          </div>
        </section>
      )}
      <div
        className={isPanelFocused ? 'search-panel-overlay-active' : 'search-panel-overlay-inactive'}
        onClick={() => isPanelFocused && setIsPanelFocused(false)}
      />
    </>
  );

  const handleOpenCalendar = () => setCalendarFocus(true);

  const handleSearchPanelClose = () => {
    setIsSearchPanelOpen(false);
  };

  const renderSearchPanelMobile = () => (
    <>
      {showBrowser && <SearchPanelBrowserMobile />}
      <main id="search-panel-mobile" className={`search-panel-container-mobile ${showBrowser ? 'hidden' : ''}`.trim()}>
        <SmlsAutoCompleteSearch
          mobile
          inputId="hotels-auto-complete-search"
          placeholder={t('smlsAutoCompleteSearch.mobilePlaceholder')}
          onFocusMobilePlaceholder={t('smlsAutoCompleteSearch.onFocusPlaceholder')}
          errorMessage={t('smlsAutoCompleteSearch.invalidField')}
          initialList={userSearchData}
          runHandleInitialList
          notFoundTitle={t('smlsAutoCompleteSearch.notFoundTitle')}
          notFoundSubtitle={t('smlsAutoCompleteSearch.notFoundSubtitle')}
          forceFocus={autoCompleteSearchFocus}
          onFocus={handleInputFocus}
          onSearch={handleInputSearch}
          onSelectedItem={handleInputSelecteditem}
          onMobileClose={handleSearchPanelClose}
          value={searchValue}
          onNextStepCrossSell={handleOpenCalendar}
          isCrossSell={hasCrossSellQuery && !!searchPanelDuck.name}
        />
        <SmlsCalendar
          hasNavHeader
          id="1"
          startDateId="2"
          endDateId="3"
          maxDate={11}
          minDate={0}
          startDatePlaceholderText={t('smlsCalendar.startDatePlaceholder')}
          endDatePlaceholderText={t('smlsCalendar.endDatePlaceholder')}
          forceFocus={calendarFocus}
          onChange={handleCalendarChange}
          onSubmit={handleCalendarSubmit}
          onPrevious={handleCalendarPrevious}
          onSkip={handleCalendarSkip}
          onMobileClose={handleSearchPanelClose}
          isOutsideRange={(day) => day.isBefore(moment().endOf('day').add(checkinAntecedence, 'days'))}
          datesListHighlight={calendarHolidaysList}
          startDateInitialValue={searchPanelDuck.checkin}
          endDateInitialValue={searchPanelDuck.checkout}
        />
        <CounterInput
          isHome
          hasHeaderReturnButton
          hasHeaderCloseButton={false}
          initialData={variant === 'results' || hasCrossSellQuery ? searchPanelDuck.rooms : undefined}
          trigger={isCounterInputOpen}
          onReturn={handleCounterInputReturn}
          onSubmit={handleCounterInputMobileSubmit}
        />
        <Icon className="search-panel-container-mobile-icon" icon="search" round />
      </main>
    </>
  );

  if (deviceWidth <= 768 && variant === 'home') {
    return renderSearchPanelMobile();
  }

  return renderSearchPanelDesktop();
};

export default SearchPanel;
